
import {
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    ResultMemberList,
    SocketMessage,
    TableData
} from 'vue'

import { useI18n } from 'vue-i18n'
import { iconX } from '@/components/icons'
import CtrlChips from '@/components/ctrl-chips.vue'
import goodroaditem from '@/components/good-road/good-road-item.vue'
import { useStore } from '@/store'
import { AppMutation } from '@/store/types'
import { getHallData, getMultiTableDetail } from '@/api'
import {
    buildPanelListData,
    collectGoodRoadTableIds,
    countGoodRoadTables,
    GoodRoadTableEntry,
    mergeMultiDetailIntoTables
} from './utils'
import axios from 'axios'
import { EventBuss, SOCKET_MESSAGE } from '@/types/global'

export default defineComponent({
    emits: ['enter-table', 'close', 'on-good-road-sync'],
    components: {
        'icon-x': iconX,
        'ctrl-chips': CtrlChips,
        'good-road-item': goodroaditem
    },
    props: {
        isFreeBaccarat: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const store = useStore()
        const emitter = inject('emitter') as Emitter
        const tabId = ref<'G002' | 'G003' | 'ALL'>('ALL')
        const prevGoodRoadIds: Set<number> = new Set()

        const { t } = useI18n()
        let countDownTimer: number | boolean = false
        let serviceTimer: number | boolean = false

        const desksByType = reactive<Record<string, TableData[]>>({
            G002: [],
            G003: [],
            ALL: []
        })

        //#region  Vue Methods
        onMounted(() => {
            loadAllData()
        })

        onBeforeMount(() => {
            // listen to websocket events
            emitter.on(EventBuss.SOCKET_MESSAGE, handleSocketMessage)
        })

        onBeforeUnmount(() => {
            // unregister events
            emitter.off(EventBuss.SOCKET_MESSAGE, handleSocketMessage)
        })
        //#endregion

        //#region socket helpers
        const resolveGameType = (
            sDesk: TableData
        ): 'G002' | 'G003' | null => {
            if (sDesk.gameTypeNo === 'G002' || sDesk.gameTypeNo === 'G003') {
                return sDesk.gameTypeNo
            }

            if ((desksByType.G002 || []).some((item) => item.id === sDesk.id)) {
                return 'G002'
            }
            if ((desksByType.G003 || []).some((item) => item.id === sDesk.id)) {
                return 'G003'
            }

            return null
        }

        const getTablesForTab = (
            tab: 'G002' | 'G003' | 'ALL'
        ): TableData[] => {
            // ALL is derived from G002/G003 so socket updates stay in sync
            if (tab === 'ALL') {
                return [
                    ...(desksByType.G002 || []),
                    ...(desksByType.G003 || [])
                ]
            }
            return desksByType[tab] || []
        }

        const handleSocketMessage = (_message: SocketMessage | any) => {
            const { type } = _message
            const _type = type.toUpperCase()

            const sDesk = _message.gameTable as TableData | undefined
            if (!sDesk) return

            const gameType = resolveGameType(sDesk)
            if (!gameType) return

            const desks = desksByType[gameType] || []
            const index = desks.findIndex((item) => item.id === sDesk.id)
            if (index === -1) return

            const updated = mergeSocketTable(desks[index], sDesk, _type)
            if (!updated) return

            desks.splice(index, 1, updated)
            emitGoodRoadSync()
        }

        //#endregion

        const mergeSocketTable = (
            current: TableData,
            sDesk: TableData,
            type: string
        ): TableData | null => {
            const _copy = { ...current, expand: { ...current.expand } }

            if (type === SOCKET_MESSAGE.NEW_GAME) {
                _copy.expand.shoeNo = sDesk.expand.shoeNo
                _copy.expand.juNo = sDesk.expand.juNo
                _copy.expand.gameNo = sDesk.expand.gameNo
                _copy.expand.daoJiShiBetTime = sDesk.expand.daoJiShiBetTime
                _copy.expand.stopBet = 0
                _copy.expand.openPoke = -1
                _copy.expand.betFormList = []
                _copy.expand.xianAmount = 0
                _copy.expand.xianDuiAmount = 0
                _copy.expand.zhuangAmount = 0
                _copy.expand.zhuangDuiAmount = 0
                _copy.expand.heAmount = 0
                _copy.expand.playerOne = undefined
                _copy.expand.playerTwo = undefined
                _copy.expand.playerThree = undefined
                _copy.expand.bankerOne = undefined
                _copy.expand.bankerTwo = undefined
                _copy.expand.bankerThree = undefined
                _copy.expand.playerCount = undefined
                _copy.expand.bankerCount = undefined
                _copy.expand.whoWin = -1
                _copy.status = 1
                _copy.timeStamp = new Date().getTime()
                return _copy
            }

            if (type === SOCKET_MESSAGE.END_GAME) {
                _copy.expand.playerOne = sDesk.expand.playerOne
                _copy.expand.playerTwo = sDesk.expand.playerTwo
                _copy.expand.playerThree = sDesk.expand.playerThree
                _copy.expand.bankerOne = sDesk.expand.bankerOne
                _copy.expand.bankerTwo = sDesk.expand.bankerTwo
                _copy.expand.bankerThree = sDesk.expand.bankerThree
                _copy.expand.playerCount = sDesk.expand.playerCount
                _copy.expand.bankerCount = sDesk.expand.bankerCount
                _copy.expand.whoWin = sDesk.expand.whoWin
                _copy.expand.resultList = sDesk.expand.resultList
                _copy.expand.resultMemberList = sDesk.expand.resultMemberList
                _copy.expand.betFormList = []
                _copy.expand.xianAmount = 0
                _copy.expand.xianDuiAmount = 0
                _copy.expand.zhuangAmount = 0
                _copy.expand.zhuangDuiAmount = 0
                _copy.expand.heAmount = 0
                _copy.timeStamp = new Date().getTime()
                return _copy
            }

            if (type === SOCKET_MESSAGE.BET || type === SOCKET_MESSAGE.GUNNER_BET) {
                // Table totals update for every bet; personal betFormList only for self
                _copy.expand.xianAmount = sDesk.expand.xianAmount
                _copy.expand.xianDuiAmount = sDesk.expand.xianDuiAmount
                _copy.expand.zhuangAmount = sDesk.expand.zhuangAmount
                _copy.expand.zhuangDuiAmount = sDesk.expand.zhuangDuiAmount
                _copy.expand.heAmount = sDesk.expand.heAmount
                _copy.expand.dragonSevenAmount = sDesk.expand.dragonSevenAmount
                _copy.expand.bearEightAmount = sDesk.expand.bearEightAmount
                _copy.expand.bigTigerAmount = sDesk.expand.bigTigerAmount
                _copy.expand.smallTigerAmount = sDesk.expand.smallTigerAmount
                _copy.expand.superSixAmount = sDesk.expand.superSixAmount

                if (sDesk.expand.memberId === memberId.value) {
                    if (
                        sDesk.expand.betFormList !== undefined &&
                        sDesk.expand.betFormList !== null
                    ) {
                        _copy.expand.betFormList = sDesk.expand.betFormList
                    }
                    if (
                        sDesk.expand.amount !== undefined &&
                        sDesk.expand.amount !== null
                    ) {
                        store.commit(AppMutation.SET_COIN, sDesk.expand.amount)
                    }
                }

                return _copy
            }

            if (type === SOCKET_MESSAGE.STOP_GAME) {
                _copy.expand.daoJiShiBetTime = 0
                _copy.status = 2
                return _copy
            }

            if (type === SOCKET_MESSAGE.STOP_BET) {
                _copy.expand.stopBet = 1
                if (
                    sDesk.expand.amount !== undefined &&
                    sDesk.expand.amount !== null
                ) {
                    store.commit(AppMutation.SET_COIN, sDesk.expand.amount)
                }
                return _copy
            }

            if (type === SOCKET_MESSAGE.CANCEL_GAME) {
                _copy.expand.betFormList = []
                _copy.status = sDesk.status

                const memberResultList = sDesk.expand.resultMemberList as
                    | ResultMemberList[]
                    | undefined
                const mine = memberResultList?.find(
                    (x) => x.memberId === memberId.value
                )
                if (mine) {
                    store.commit(AppMutation.SET_COIN, mine.amount)
                }
                return _copy
            }

            if (type === SOCKET_MESSAGE.NEW_SHOE) {
                sDesk.expand.betFormList = []
                const {
                    downLimit,
                    upLimit,
                    limitMax,
                    pairDownLimit,
                    pairUpLimit,
                    tieDownLimit,
                    tieUpLimit,
                    superSixDownLimit,
                    superSixUpLimit
                } = _copy

                sDesk.downLimit = downLimit
                sDesk.upLimit = upLimit
                sDesk.limitMax = limitMax
                sDesk.pairDownLimit = pairDownLimit
                sDesk.pairUpLimit = pairUpLimit
                sDesk.tieDownLimit = tieDownLimit
                sDesk.tieUpLimit = tieUpLimit
                sDesk.superSixDownLimit = superSixDownLimit
                sDesk.superSixUpLimit = superSixUpLimit
                sDesk.expand.resultList = []

                return {
                    ..._copy,
                    ...sDesk,
                    expand: {
                        ..._copy.expand,
                        ...sDesk.expand
                    }
                }
            }

            return null
        }

        const loadAllData = (forceReload = false): void => {
            if (!forceReload) store.commit(AppMutation.SHOW_LOADING)

            getHallData({
                token: token.value,
                commandType: userCommandType.value
            })
                .then((response) => {
                    const responseData = response.data as {
                        status?: number
                        data?: { gameTableList?: TableData[] }
                    }
                    if (responseData?.status !== 200) {
                        if (responseData?.status === 301) {
                            stopApiRequest()
                            emitter.emit(EventBuss.SESSION_EXPIRED)
                        }
                        return Promise.reject(
                            new Error('failed to load hall data')
                        )
                    }


                    const hallTables = responseData?.data?.gameTableList || []
                    desksByType.G002 = hallTables.filter(
                        (table: TableData) => table.gameTypeNo === 'G002'
                    )
                    desksByType.G003 = hallTables.filter(
                        (table: TableData) => table.gameTypeNo === 'G003'
                    )
                    desksByType.ALL = [
                        ...(desksByType.G002 || []),
                        ...(desksByType.G003 || [])
                    ]

                    return fetchAndMergeMultiDetail()
                })
                .then(() => {
                    if (!forceReload) store.commit(AppMutation.HIDE_LOADING)
                    emitGoodRoadSync()
                    cancelCountDown()
                    startCountDown()
                })
                .catch(() => {
                    if (!forceReload) store.commit(AppMutation.HIDE_LOADING)
                })
        }

        const fetchAndMergeMultiDetail = (): Promise<void> => {
            const tableIds = collectGoodRoadTableIds(desksByType)

            // console.log('TABLEIDS', desksByType)

            return getMultiTableDetail({
                token: token.value,
                memberId: memberId.value,
                tableIds: tableIds.join(',')
            })
                .then((r) => {
                    const response = r.data
                    if (response.status !== 200) {
                        if (response.status === 301) {
                            stopApiRequest()
                            emitter.emit(EventBuss.SESSION_EXPIRED)
                        }
                        return
                    }

                    const detailTables = response.data.gameTableList || []
                    if (!detailTables.length) return

                        // Only merge into G002/G003; ALL is derived from those
                        ; (['G002', 'G003'] as const).forEach((gameTypeNo) => {
                            desksByType[gameTypeNo] = mergeMultiDetailIntoTables(
                                desksByType[gameTypeNo] || [],
                                detailTables
                            )
                        })
                    desksByType.ALL = [
                        ...(desksByType.G002 || []),
                        ...(desksByType.G003 || [])
                    ]
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return

                    throw error
                })
        }

        const stopApiRequest = () => {
            cancelCountDown()
            if (typeof serviceTimer === 'number') {
                clearTimeout(serviceTimer)
                serviceTimer = false
            }
        }

        const emitGoodRoadSync = () => {
            emit('on-good-road-sync', {
                G002: desksByType.G002 || [],
                G003: desksByType.G003 || [],
                ALL: getTablesForTab('ALL')
            })
            // console.log('emit sync')
        }

        const cancelCountDown = () => {
            if (typeof countDownTimer === 'number') {
                clearTimeout(countDownTimer)
                countDownTimer = false
            }
        }
        const startCountDown = () => {
            if (typeof countDownTimer === 'number') return

            doCountDown()
        }

        const doCountDown = () => {
            countDownTimer = window.setTimeout(() => {
                // Only tick source arrays (G002/G003). ALL is derived from them.
                const sourceTypes = ['G002', 'G003'] as const
                sourceTypes.forEach((gameTypeNo) => {
                    const desks = desksByType[gameTypeNo] || []

                    desks.forEach((item) => {
                        if (item.expand.daoJiShiBetTime > 0)
                            item.expand.daoJiShiBetTime -= 1
                        else if (item.expand.daoJiShiBetTime === 0) {
                            item.expand.daoJiShiBetTime = -1
                            item.status = 2
                        }
                    })
                })

                doCountDown()
            }, 1000)
        }

        const switchTab = (gType: 'G002' | 'G003' | 'ALL') => {
            tabId.value = gType
        }

        const listData = computed((): GoodRoadTableEntry[] => {
            const entries = buildPanelListData(getTablesForTab(tabId.value))

            // collect current good-road table ids
            const currentGoodIds = entries
                .filter((e) => e.goodRoads.length > 0)
                .map((e) => e.table.id)

            // newly detected good roads = present now but not in previous render
            const newGoodIdSet = new Set<number>(
                currentGoodIds.filter((id) => !prevGoodRoadIds.has(id))
            )

            // prioritize newly detected good-road entries while preserving relative order
            const newGoodEntries = entries.filter((e) =>
                newGoodIdSet.has(e.table.id)
            )
            const otherEntries = entries.filter(
                (e) => !newGoodIdSet.has(e.table.id)
            )

            prevGoodRoadIds.clear()
            currentGoodIds.forEach((id) => prevGoodRoadIds.add(id))

            return newGoodEntries.concat(otherEntries)
        })

        const tabGoodRoadCounts = computed(() => ({
            ALL: countGoodRoadTables(getTablesForTab('ALL')),
            G002: countGoodRoadTables(getTablesForTab('G002')),
            G003: countGoodRoadTables(getTablesForTab('G003'))
        }))

        const token = computed(() => store.getters['token'])
        const memberId = computed(() => store.getters['memberid'])
        const userCommandType = computed(() => store.getters['commandType'])

        return {
            tabId,
            switchTab,
            t,
            listData,
            tabGoodRoadCounts
        }
    }
})
